import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Grid, Input, Modal, Spin, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccountInfo } from 'hooks/useAccount';
import { useGetAssignedFarmUsersQuery, useInviteFarmUsers } from 'hooks/useFarms';
import { useSelfRegisterPostResendToken } from 'hooks/useRegistration';
import { storageKeys } from 'static';
import { setAnalyticsFarmContext } from 'analytics/ga4';
import { canInviteFarmUsers } from 'farmInviteAccess';
import { AccountInfoData, AssignedFarmUser, GetAssignedFarmUsersData, TFunction } from 'types';

const { useBreakpoint } = Grid;

const cardBorder = 'none';
const cardShadow = '2px 4px 4px 0px rgba(204, 204, 204, 0.25)';
const cardRadius = 8;
const modalButtonHeight = 46;
const pageContentMaxWidth = 1440;
const inviteActionButtonStyle = {
  height: 46,
  borderRadius: 12,
  backgroundColor: '#4A4D4E',
  borderColor: '#4A4D4E',
};

const resendInviteActionButtonStyle = {
  height: 46,
  borderRadius: 12,
  backgroundColor: '#4A4D4E',
  borderColor: '#4A4D4E',
};

const getRecentFarmId = () => sessionStorage.getItem(storageKeys.RecentFarmId);

const getStringValue = (value: string | number | null | undefined) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const getFarmIdFromAccountInfo = (accountInfo?: AccountInfoData) => {
  const candidates = [
    accountInfo?.selectedFarmId,
    accountInfo?.userFarmRoles?.[0]?.farmId,
    accountInfo?.userFarms?.[0]?.id,
    accountInfo?.userFarms?.[0]?.farmId,
  ];

  const farmId = candidates.find((candidate) => getStringValue(candidate).length > 0);
  return farmId ? String(farmId) : null;
};

const getFarmNameFromAccountInfo = (farmId: string | null, accountInfo?: AccountInfoData) => {
  const normalizedFarmId = getStringValue(farmId);

  if (normalizedFarmId) {
    const matchingFarm = (accountInfo?.userFarms ?? []).find((farm) => {
      const candidates = [farm?.id, farm?.farmId];
      return candidates.some((candidate) => getStringValue(candidate) === normalizedFarmId);
    });

    const matchingFarmName = getStringValue(matchingFarm?.farmName);
    if (matchingFarmName) {
      return matchingFarmName;
    }

    const matchingFarmRole = (accountInfo?.userFarmRoles ?? []).find((farmRole) => {
      return getStringValue(farmRole?.farmId) === normalizedFarmId;
    });

    const matchingFarmRoleName = getStringValue(matchingFarmRole?.farmName);
    if (matchingFarmRoleName) {
      return matchingFarmRoleName;
    }
  }

  return getStringValue(accountInfo?.selectedFarmName);
};

const getFarmOrgNumberFromAccountInfo = (farmId: string | null, accountInfo?: AccountInfoData) => {
  const normalizedFarmId = getStringValue(farmId);

  if (!normalizedFarmId) {
    return '';
  }

  const matchingFarm = (accountInfo?.userFarms ?? []).find((farm) => {
    const candidates = [farm?.id, farm?.farmId, farm?.farmCode];
    return candidates.some((candidate) => getStringValue(candidate) === normalizedFarmId);
  });

  return getStringValue(matchingFarm?.companyRegistration);
};

const getUserInitials = (value: string) => {
  const initials = value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  return initials || 'U';
};

const parseInviteEmails = (value: string | undefined) =>
  Array.from(
    new Set(
      getStringValue(value)
        .split(/[\n,;]+/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    )
  );

const isValidEmail = (value?: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

const normalizeAssignedUser = (user: AssignedFarmUser, index: number, fallback: string) => {
  const email = getStringValue(user.email) || fallback;
  const firstName = getStringValue(user.firstName);
  const lastName = getStringValue(user.lastName);

  return {
    email,
    firstName,
    isActive: typeof user.isActive === 'boolean' ? user.isActive : null,
    isValid: typeof user.isValid === 'boolean' ? user.isValid : null,
    key: `${email}-${index}`,
    language: getStringValue(user.language),
    lastName,
  };
};

const extractAssignedUsers = (data?: GetAssignedFarmUsersData | null): AssignedFarmUser[] => {
  if (Array.isArray(data)) {
    return data;
  }

  const users = data?.users;
  if (Array.isArray(users)) {
    return users;
  }

  return [];
};

type NormalizedAssignedUser = ReturnType<typeof normalizeAssignedUser>;

const getUserDisplayName = (firstName: string, lastName: string, fallback: string) => {
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || fallback;
};

const getUserStatusLabel = (isActive: boolean | null, t: TFunction['t'], fallback: string) => {
  if (isActive === true) {
    return t('Active');
  }

  if (isActive === false) {
    return t('Inactive');
  }

  return fallback;
};

const getUserLanguageLabel = (value: string, t: TFunction['t'], fallback: string) => {
  const normalizedValue = getStringValue(value).toLowerCase();

  if (!normalizedValue) {
    return fallback;
  }

  if (normalizedValue === 'se' || normalizedValue === 'sv') {
    return t('LanguageSwedish');
  }

  if (normalizedValue === 'en') {
    return t('LanguageEnglish');
  }

  return normalizedValue.toUpperCase();
};

function SummaryStat({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: cardRadius,
        background: 'rgba(255, 255, 255, 0.86)',
        border: 'none',
        boxShadow: cardShadow,
        backdropFilter: 'blur(8px)',
      }}
    >
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.9,
          color: '#98a2b3',
          marginBottom: 8,
        }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 16,
          lineHeight: 1.4,
          color: '#101828',
          fontWeight: 700,
        }}
      >
        {value}
      </Typography.Text>
    </div>
  );
}

function StatusBadge({
  isActive,
  label,
}: {
  isActive: boolean | null;
  label: string;
}) {
  const isPositive = isActive === true;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 12px',
        borderRadius: 999,
        background: isPositive ? '#ecfdf3' : '#f8fafc',
        border: isPositive ? '1px solid rgba(18, 183, 106, 0.2)' : '1px solid rgba(152, 162, 179, 0.2)',
        color: isPositive ? '#027a48' : '#475467',
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isPositive ? '#12b76a' : '#98a2b3',
        }}
      />
      {label}
    </span>
  );
}

export default function UsersPage() {
  const { t }: TFunction = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [inviteForm] = Form.useForm();
  const { data: accountInfo, isLoading: isLoadingAccountInfo, isError: isErrorAccountInfo } = useAccountInfo();
  const { mutate: inviteFarmUsers, isLoading: isInvitingFarmUsers } = useInviteFarmUsers();
  const { mutate: resendInvite, isLoading: isResendingInvite } = useSelfRegisterPostResendToken();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [resendingInviteEmail, setResendingInviteEmail] = useState<string | null>(null);

  const isCompact = !screens.md;
  const useSplitLayout = !!screens.lg;
  const fallback = t('NotAvailable');
  const farmId = getRecentFarmId() || getFarmIdFromAccountInfo(accountInfo);
  const inviteFarmUsersAllowed = canInviteFarmUsers(accountInfo);
  const {
    data: assignedUsersData,
    isLoading: isLoadingAssignedUsers,
    isError: isErrorAssignedUsers,
    refetch: refetchAssignedUsers,
  } = useGetAssignedFarmUsersQuery(farmId);
  const shouldOpenInviteModal = location.state?.openInviteModal === true;

  const handleResendInvite = (email: string) => {
    const normalizedEmail = getStringValue(email);

    if (!isValidEmail(normalizedEmail)) {
      messageApi.error(t('RequireAValidEmail'));
      return;
    }

    setResendingInviteEmail(normalizedEmail.toLowerCase());

    resendInvite(
      { registeredEmail: normalizedEmail },
      {
        onSuccess: () => {
          messageApi.success(t('UsersPageResendInviteSuccess'));
        },
        onSettled: () => {
          setResendingInviteEmail(null);
        },
      }
    );
  };

  useEffect(() => {
    if (!farmId) return;

    sessionStorage.setItem(storageKeys.RecentFarmId, farmId);
    const farmName = getFarmNameFromAccountInfo(farmId, accountInfo);
    const orgNumber = getFarmOrgNumberFromAccountInfo(farmId, accountInfo);
    if (farmName) {
      sessionStorage.setItem(storageKeys.RecentFarmName, farmName);
    }
    if (orgNumber) {
      sessionStorage.setItem(storageKeys.RecentFarmOrgNumber, orgNumber);
    }
    setAnalyticsFarmContext(farmId, farmName || undefined, orgNumber || undefined);
  }, [farmId, accountInfo]);

  useEffect(() => {
    if (shouldOpenInviteModal && farmId && inviteFarmUsersAllowed) {
      setIsInviteModalOpen(true);
    }
  }, [farmId, inviteFarmUsersAllowed, shouldOpenInviteModal]);

  const assignedUsers = extractAssignedUsers(assignedUsersData);
  const users = assignedUsers.map((user, index) => normalizeAssignedUser(user, index, t('UsersPageUnknownUser')));
  const sortedUsers = [...users].sort((left, right) => {
    const leftLabel = `${left.firstName} ${left.lastName} ${left.email}`.trim();
    const rightLabel = `${right.firstName} ${right.lastName} ${right.email}`.trim();

    return leftLabel.localeCompare(rightLabel, 'sv');
  });
  const registeredUsers = sortedUsers.filter((user) => user.isActive === true || user.isValid !== false);
  const invitedUsers = sortedUsers.filter((user) => user.isValid === false && user.isActive !== true);
  const tableColumns = [
    t('Email'),
    t('FirstName'),
    t('LastName'),
    t('Status'),
    t('UsersPageLanguage'),
  ];
  const invitedTableColumns = [
    t('Email'),
    t('FirstName'),
    t('LastName'),
    t('UsersPageLanguage'),
    t('UsersPageResendInvite'),
  ];

  if (isLoadingAccountInfo || (!!farmId && isLoadingAssignedUsers)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 72, paddingBottom: 48 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      </div>
    );
  }

  if (!farmId) {
    return (
      <div style={{ paddingTop: 16, paddingBottom: 40 }}>
        {messageContextHolder}
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            background: '#fff',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '28px 20px' : '36px 32px',
            textAlign: 'center',
          }}
        >
          <Typography.Title
            level={2}
            style={{
              marginTop: 0,
              marginBottom: 10,
              color: '#101828',
            }}
          >
            {t('SectionNavigationUsers')}
          </Typography.Title>
          <Typography.Paragraph
            style={{
              color: '#475467',
              marginBottom: 24,
              maxWidth: 520,
              marginInline: 'auto',
            }}
          >
            {t('UsersPageNoFarmText')}
          </Typography.Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              width: isCompact ? '100%' : 'auto',
              minWidth: 180,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#ff6b3d',
              borderColor: '#ff6b3d',
            }}
            onClick={() => navigate('/register-farm', { replace: true })}
          >
            {t('RegisterFarmButton')}
          </Button>
        </div>
      </div>
    );
  }

  if (isErrorAccountInfo || (!!farmId && isErrorAssignedUsers)) {
    return (
      <div style={{ paddingTop: 16, paddingBottom: 40 }}>
        {messageContextHolder}
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            background: '#fff',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '28px 20px' : '36px 32px',
            textAlign: 'center',
          }}
        >
          <Typography.Title level={1} style={{ marginBottom: 12 }}>
            {t('AnErrorOccurred')}
          </Typography.Title>
          <Typography.Paragraph style={{ color: '#6b7280', marginBottom: 0, maxWidth: 460, marginInline: 'auto' }}>
            {t('UsersPageErrorText')}
          </Typography.Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 8, paddingBottom: 40 }}>
      {messageContextHolder}
      <div style={{ maxWidth: pageContentMaxWidth, margin: '0 auto' }}>
        <section
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 58%, #fff5ef 100%)',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '26px 20px' : '34px 32px',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -100,
              right: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 107, 61, 0.18) 0%, rgba(255, 107, 61, 0) 72%)',
            }}
          />
          <div
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: useSplitLayout ? 'minmax(0, 1.2fr) minmax(320px, 0.8fr)' : '1fr',
              gap: 24,
              alignItems: 'start',
            }}
          >
            <div>
              <Typography.Text
                style={{
                  display: 'inline-block',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.1,
                  color: '#98a2b3',
                  marginBottom: 12,
                }}
              >
                {t('SectionNavigationUsers')}
              </Typography.Text>
              <Typography.Title
                level={1}
                style={{
                  marginTop: 0,
                  marginBottom: 10,
                  fontSize: isCompact ? 30 : 38,
                  lineHeight: 1.08,
                  color: '#101828',
                }}
              >
                {t('SectionNavigationUsers')}
              </Typography.Title>
              <Typography.Paragraph
                style={{
                  maxWidth: 560,
                  marginBottom: 0,
                  color: '#475467',
                  fontSize: 15,
                  lineHeight: 1.6,
                }}
              >
                {t('UsersPageText')}
              </Typography.Paragraph>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 12,
              }}
            >
              <SummaryStat label={t('UsersPageTotalUsers')} value={String(sortedUsers.length)} />
            </div>
          </div>

          {inviteFarmUsersAllowed ? (
            <div
              style={{
                position: 'relative',
                marginTop: 20,
                display: 'flex',
                justifyContent: isCompact ? 'stretch' : 'flex-start',
              }}
            >
              <Button
                type="primary"
                size="large"
                style={{
                  ...inviteActionButtonStyle,
                  width: isCompact ? '100%' : 'auto',
                }}
                onClick={() => setIsInviteModalOpen(true)}
              >
                {t('UsersPageInviteButton')}
              </Button>
            </div>
          ) : (
            <Typography.Paragraph
              style={{
                marginTop: 20,
                marginBottom: 0,
                maxWidth: 560,
                color: '#667085',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {t('UsersPageInviteUnavailable')}
            </Typography.Paragraph>
          )}
        </section>

        {!sortedUsers.length ? (
          <section
            style={{
              background: '#fff',
              borderRadius: cardRadius,
              border: cardBorder,
              boxShadow: cardShadow,
              padding: isCompact ? '28px 20px' : '34px 32px',
              textAlign: 'center',
            }}
          >
            <Typography.Title level={3} style={{ marginBottom: 8, color: '#101828' }}>
              {t('NoUsersYet')}
            </Typography.Title>
            <Typography.Paragraph style={{ color: '#6b7280', marginBottom: 0 }}>
              {t('UsersPageText')}
            </Typography.Paragraph>
          </section>
        ) : (
          <>
            {registeredUsers.length ? (
              <section
                style={{
                  background: '#fff',
                  borderRadius: cardRadius,
                  border: cardBorder,
                  boxShadow: cardShadow,
                  overflow: 'hidden',
                  marginBottom: invitedUsers.length ? 20 : 0,
                }}
              >
                {isCompact ? (
                  <div style={{ paddingInline: 18 }}>
                    {registeredUsers.map((user, index) => {
                      const email = getStringValue(user.email) || fallback;
                      const firstName = getStringValue(user.firstName) || fallback;
                      const lastName = getStringValue(user.lastName) || fallback;
                      const language = getUserLanguageLabel(getStringValue(user.language), t, fallback);
                      const status = getUserStatusLabel(user.isActive, t, fallback);
                      const displayName = getUserDisplayName(
                        getStringValue(user.firstName),
                        getStringValue(user.lastName),
                        email
                      );
                      const shouldShowSecondaryEmail = displayName !== email;

                      return (
                        <div
                          key={user.key}
                          style={{
                            padding: '18px 0',
                            borderBottom:
                              index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              alignItems: 'flex-start',
                              marginBottom: 14,
                            }}
                          >
                            <div style={{ display: 'flex', gap: 12, minWidth: 0, alignItems: 'center' }}>
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #ffede6 0%, #fff7f0 100%)',
                                  color: '#ff6b3d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 14,
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {getUserInitials(displayName)}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    color: '#101828',
                                    fontSize: 15,
                                    lineHeight: 1.4,
                                    fontWeight: 700,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {displayName}
                                </Typography.Text>
                                {shouldShowSecondaryEmail ? (
                                  <Typography.Text
                                    style={{
                                      display: 'block',
                                      color: '#667085',
                                      fontSize: 13,
                                      marginTop: 2,
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {email}
                                  </Typography.Text>
                                ) : null}
                              </div>
                            </div>
                            <StatusBadge isActive={user.isActive} label={status} />
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: screens.sm ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                              gap: 12,
                            }}
                          >
                            {[
                              { label: t('Email'), value: email },
                              { label: t('FirstName'), value: firstName },
                              { label: t('LastName'), value: lastName },
                              { label: t('UsersPageLanguage'), value: language },
                            ].map((item) => (
                              <div key={`${user.key}-${item.label}`} style={{ minWidth: 0 }}>
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.9,
                                    color: '#98a2b3',
                                    marginBottom: 4,
                                  }}
                                >
                                  {item.label}
                                </Typography.Text>
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    fontSize: 14,
                                    lineHeight: 1.45,
                                    color: item.value === fallback ? '#98a2b3' : '#101828',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {item.value}
                                </Typography.Text>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        minWidth: 760,
                        borderCollapse: 'collapse',
                      }}
                    >
                      <thead>
                        <tr>
                          {tableColumns.map((column) => (
                            <th
                              key={column}
                              style={{
                                padding: '16px 20px',
                                textAlign: 'left',
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.9,
                                color: '#98a2b3',
                                background: '#f8fafc',
                                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                              }}
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {registeredUsers.map((user: NormalizedAssignedUser, index) => {
                          const email = getStringValue(user.email) || fallback;
                          const firstName = getStringValue(user.firstName) || fallback;
                          const lastName = getStringValue(user.lastName) || fallback;
                          const language = getUserLanguageLabel(getStringValue(user.language), t, fallback);
                          const status = getUserStatusLabel(user.isActive, t, fallback);

                          return (
                            <tr key={user.key}>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: email === fallback ? '#98a2b3' : '#101828',
                                  fontWeight: 600,
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {email}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: firstName === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {firstName}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: lastName === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {lastName}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  verticalAlign: 'middle',
                                }}
                              >
                                <StatusBadge isActive={user.isActive} label={status} />
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === registeredUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: language === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {language}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ) : null}

            {invitedUsers.length ? (
              <section
                style={{
                  background: '#fff',
                  borderRadius: cardRadius,
                  border: cardBorder,
                  boxShadow: cardShadow,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: isCompact ? '18px 18px 12px' : '18px 20px 12px',
                    borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                    background: '#f8fafc',
                  }}
                >
                  <Typography.Title level={3} style={{ margin: 0, color: '#101828' }}>
                    {t('UsersPageInvitedTitle')}
                  </Typography.Title>
                </div>

                {isCompact ? (
                  <div style={{ paddingInline: 18 }}>
                    {invitedUsers.map((user, index) => {
                      const email = getStringValue(user.email) || fallback;
                      const firstName = getStringValue(user.firstName) || fallback;
                      const lastName = getStringValue(user.lastName) || fallback;
                      const language = getUserLanguageLabel(getStringValue(user.language), t, fallback);
                      const isRowResending = isResendingInvite && resendingInviteEmail === email.toLowerCase();
                      const displayName = getUserDisplayName(
                        getStringValue(user.firstName),
                        getStringValue(user.lastName),
                        email
                      );
                      const shouldShowSecondaryEmail = displayName !== email;

                      return (
                        <div
                          key={`${user.key}-invited`}
                          style={{
                            padding: '18px 0',
                            borderBottom:
                              index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              alignItems: 'flex-start',
                              marginBottom: 14,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <Typography.Text
                                style={{
                                  display: 'block',
                                  color: '#101828',
                                  fontSize: 15,
                                  lineHeight: 1.4,
                                  fontWeight: 700,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {displayName}
                              </Typography.Text>
                              {shouldShowSecondaryEmail ? (
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    color: '#667085',
                                    fontSize: 13,
                                    marginTop: 2,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {email}
                                </Typography.Text>
                              ) : null}
                            </div>
                            <Button
                              type="primary"
                              size="large"
                              loading={isRowResending}
                              disabled={!isValidEmail(email) || (isResendingInvite && !isRowResending)}
                              style={resendInviteActionButtonStyle}
                              onClick={() => handleResendInvite(email)}
                            >
                              {t('UsersPageResendInvite')}
                            </Button>
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: screens.sm ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                              gap: 12,
                            }}
                          >
                            {[
                              { label: t('Email'), value: email },
                              { label: t('FirstName'), value: firstName },
                              { label: t('LastName'), value: lastName },
                              { label: t('UsersPageLanguage'), value: language },
                            ].map((item) => (
                              <div key={`${user.key}-invited-${item.label}`} style={{ minWidth: 0 }}>
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.9,
                                    color: '#98a2b3',
                                    marginBottom: 4,
                                  }}
                                >
                                  {item.label}
                                </Typography.Text>
                                <Typography.Text
                                  style={{
                                    display: 'block',
                                    fontSize: 14,
                                    lineHeight: 1.45,
                                    color: item.value === fallback ? '#98a2b3' : '#101828',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {item.value}
                                </Typography.Text>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        minWidth: 760,
                        borderCollapse: 'collapse',
                      }}
                    >
                      <thead>
                        <tr>
                          {invitedTableColumns.map((column) => (
                            <th
                              key={column}
                              style={{
                                padding: '16px 20px',
                                textAlign: 'left',
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.9,
                                color: '#98a2b3',
                                background: '#f8fafc',
                                borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                              }}
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {invitedUsers.map((user: NormalizedAssignedUser, index) => {
                          const email = getStringValue(user.email) || fallback;
                          const firstName = getStringValue(user.firstName) || fallback;
                          const lastName = getStringValue(user.lastName) || fallback;
                          const language = getUserLanguageLabel(getStringValue(user.language), t, fallback);
                          const isRowResending = isResendingInvite && resendingInviteEmail === email.toLowerCase();

                          return (
                            <tr key={`${user.key}-invited`}>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: email === fallback ? '#98a2b3' : '#101828',
                                  fontWeight: 600,
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {email}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: firstName === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {firstName}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: lastName === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {lastName}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  color: language === fallback ? '#98a2b3' : '#101828',
                                  wordBreak: 'break-word',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {language}
                              </td>
                              <td
                                style={{
                                  padding: '18px 20px',
                                  borderBottom:
                                    index === invitedUsers.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                                  verticalAlign: 'middle',
                                }}
                              >
                                <Button
                                  type="primary"
                                  size="large"
                                  loading={isRowResending}
                                  disabled={!isValidEmail(email) || (isResendingInvite && !isRowResending)}
                                  style={resendInviteActionButtonStyle}
                                  onClick={() => handleResendInvite(email)}
                                >
                                  {t('UsersPageResendInvite')}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ) : null}
          </>
        )}
      </div>

      <Modal
        title={
          <Typography.Title
            level={2}
            style={{
              margin: 0,
              color: '#101828',
              fontSize: isCompact ? 22 : 26,
              lineHeight: 1.1,
            }}
          >
            {t('UsersPageInviteTitle')}
          </Typography.Title>
        }
        open={isInviteModalOpen}
        onCancel={() => {
          setIsInviteModalOpen(false);
          inviteForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={isCompact ? 'calc(100vw - 24px)' : 620}
        styles={{
          content: {
            borderRadius: 24,
            padding: isCompact ? 20 : 28,
            background: '#ffffff',
            boxShadow: '0 22px 48px rgba(29, 33, 41, 0.14)',
            border: '1px solid rgba(255, 107, 61, 0.14)',
          },
          header: {
            background: 'transparent',
            marginBottom: 8,
            padding: 0,
          },
          body: {
            padding: 0,
          },
        }}
      >
        <Typography.Paragraph
          style={{
            color: '#475467',
            marginBottom: 16,
            fontSize: isCompact ? 15 : 16,
            lineHeight: 1.6,
          }}
        >
          {t('UsersPageInviteText')}
        </Typography.Paragraph>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('UsersPageInviteHint')}
        />
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={(values: { emails: string }) => {
            const emails = parseInviteEmails(values.emails);

            inviteFarmUsers(emails, {
              onSuccess: () => {
                messageApi.success(t('UsersPageInviteSuccess', { count: emails.length }));
                setIsInviteModalOpen(false);
                inviteForm.resetFields();
                if (farmId) {
                  void refetchAssignedUsers();
                }
              },
            });
          }}
        >
          <Form.Item
            name="emails"
            label={(
              <span
                style={{
                  color: '#344054',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {t('UsersPageInviteField')}
              </span>
            )}
            rules={[
              {
                validator: async (_, value) => {
                  const emails = parseInviteEmails(value);

                  if (!emails.length) {
                    return Promise.reject(new Error(t('EmailIsRequired')));
                  }

                  const hasInvalidEmail = emails.some(
                    (email) => !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
                  );

                  if (hasInvalidEmail) {
                    return Promise.reject(new Error(t('RequireAValidEmail')));
                  }
                },
              },
            ]}
          >
            <Input.TextArea
              rows={5}
              placeholder={t('UsersPageInvitePlaceholder')}
              style={{
                borderRadius: 12,
                padding: '12px 14px',
              }}
            />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button
              size="large"
              style={{
                height: modalButtonHeight,
                minWidth: isCompact ? 0 : 132,
                borderRadius: 12,
                paddingInline: 18,
                fontSize: 15,
                fontWeight: 600,
              }}
              onClick={() => {
                setIsInviteModalOpen(false);
                inviteForm.resetFields();
              }}
            >
              {t('Back')}
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={isInvitingFarmUsers}
              style={{
                height: modalButtonHeight,
                minWidth: isCompact ? 0 : 160,
                borderRadius: 12,
                paddingInline: 18,
                fontSize: 15,
                fontWeight: 600,
                backgroundColor: '#ff6b3d',
                borderColor: '#ff6b3d',
              }}
            >
              {t('UsersPageInviteSubmit')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
